import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Check, Building2, Briefcase, DollarSign, Shield, Linkedin, Mail, Loader2, CheckCircle, XCircle, FileText, Upload, Menu, X } from "lucide-react";
import ThemeToggle from "../components/ThemeToggle";
import { triggerSuccessConfetti } from "../components/Confetti";

const formatCurrency = (value) => {
  if (!value) return "0";
  if (value >= 10000000) {
    return `${(value / 10000000).toFixed(2)} Cr`;
  } else if (value >= 100000) {
    return `${(value / 100000).toFixed(2)} L`;
  }
  return value.toLocaleString('en-IN');
};

const SubmitPlacement = () => {
  const { user, login, loading: authLoading, sessionLoading } = useAuth();
  const navigate = useNavigate();
  
  const [step, setStep] = useState(1);
  const [colleges, setColleges] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleMobileNav = (path) => {
    setMobileMenuOpen(false);
    navigate(path);
  };
  
  // Form data
  const [formData, setFormData] = useState({
    college_id: "",
    college_name: "", // Added for autocomplete
    program_id: "",
    company: "",
    role: "",
    fixed: "",
    variable: "",
    esops: "",
    joining_bonus: "",
    relocation: "",
    year: new Date().getFullYear().toString(),
    placement_type: "placement",
    is_ppo: false,
    internship_stipend: "",
    internship_duration: "2",
    // Verification fields
    linkedin_url: "",
    college_email: "",
    company_email: ""
  });
  
  // College autocomplete state
  const [collegeSearchQuery, setCollegeSearchQuery] = useState("");
  const [collegeSuggestions, setCollegeSuggestions] = useState([]);
  const [showCollegeSuggestions, setShowCollegeSuggestions] = useState(false);
  const [loadingColleges, setLoadingColleges] = useState(false);
  const [collegePrograms, setCollegePrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const collegeInputRef = useRef(null);
  
  // Offer letter file
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [uploadingOfferLetter, setUploadingOfferLetter] = useState(false);
  const [offerLetterUrl, setOfferLetterUrl] = useState(null);
  const [extractedSalary, setExtractedSalary] = useState(null);
  const [salaryBreakdown, setSalaryBreakdown] = useState(null);
  const [offerDetails, setOfferDetails] = useState(null);
  const [fraudAnalysis, setFraudAnalysis] = useState(null);
  const [salaryAutoFilled, setSalaryAutoFilled] = useState(false);
  const [companyMismatchError, setCompanyMismatchError] = useState(null);
  
  // CTC Verification state
  const [ctcVerification, setCtcVerification] = useState(null);
  const [verifyingCtc, setVerifyingCtc] = useState(false);
  
  // Verification method choice
  const [verificationMethod, setVerificationMethod] = useState(null); // 'manual' or 'offer_letter'
  
  // Verification state
  const [placementId, setPlacementId] = useState(null);
  const [verificationState, setVerificationState] = useState({
    linkedin_verified: false,
    linkedin_error: null,
    company_verified: false,
    role_verified: false,
    company_email_verified: false,
    company_otp_sent: false,
    company_otp: "",
    verifying_linkedin: false,
    verifying_company_otp: false,
    sending_otp: false,
    linkedin_profile_opened: false, // Track if user opened the profile
    linkedin_confirmation_step: false // Track if user is in confirmation step
  });

  // Company email domain validation
  const [emailDomainError, setEmailDomainError] = useState(null);
  const [emailNameError, setEmailNameError] = useState(null);
  const [linkedinNameError, setLinkedinNameError] = useState(null);
  
  // Helper function to extract name parts from user's full name
  const getUserNameParts = () => {
    if (!user?.name) return { firstName: '', lastName: '', fullName: '' };
    
    const fullName = user.name.toLowerCase().trim();
    const parts = fullName.split(/\s+/);
    const firstName = parts[0] || '';
    const lastName = parts.length > 1 ? parts[parts.length - 1] : '';
    
    return { firstName, lastName, fullName };
  };
  
  // Function to check if email contains user's name
  const validateEmailContainsName = (email) => {
    if (!email || !user?.name) return true; // Skip if no email or no user
    
    const { firstName, lastName } = getUserNameParts();
    if (!firstName && !lastName) return true;
    
    // Extract local part of email (before @)
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return true;
    
    const localPart = email.substring(0, atIndex).toLowerCase();
    
    // Check if local part contains first name or last name (min 3 chars to avoid false positives)
    const containsFirstName = firstName.length >= 3 && localPart.includes(firstName);
    const containsLastName = lastName.length >= 3 && localPart.includes(lastName);
    
    // Also check for common patterns like first initial + last name
    const firstInitial = firstName.charAt(0);
    const containsInitialLastName = lastName.length >= 3 && localPart.includes(firstInitial + lastName);
    const containsFirstInitial = firstName.length >= 3 && localPart.includes(firstName + lastName.charAt(0));
    
    return containsFirstName || containsLastName || containsInitialLastName || containsFirstInitial;
  };
  
  // Function to check if LinkedIn URL contains user's name
  const validateLinkedInContainsName = (linkedinUrl) => {
    if (!linkedinUrl || !user?.name) return true;
    
    const { firstName, lastName } = getUserNameParts();
    if (!firstName && !lastName) return true;
    
    // Extract the profile slug from LinkedIn URL
    const match = linkedinUrl.toLowerCase().match(/linkedin\.com\/in\/([^\/\?]+)/);
    if (!match) return true;
    
    const profileSlug = match[1].replace(/[-_]/g, ''); // Remove hyphens and underscores
    
    // Check if slug contains first name or last name (min 3 chars)
    const containsFirstName = firstName.length >= 3 && profileSlug.includes(firstName);
    const containsLastName = lastName.length >= 3 && profileSlug.includes(lastName);
    
    return containsFirstName || containsLastName;
  };
  
  // Function to validate if company email matches company name
  const validateCompanyEmailDomain = (companyName, companyEmail) => {
    if (!companyName || !companyEmail) {
      setEmailDomainError(null);
      return true;
    }
    
    // Extract domain from email
    const emailParts = companyEmail.toLowerCase().split('@');
    if (emailParts.length !== 2) {
      setEmailDomainError("Invalid email format");
      return false;
    }
    
    const emailDomain = emailParts[1].split('.')[0]; // Get first part of domain (e.g., "google" from "google.com")
    const companyLower = companyName.toLowerCase().trim();
    
    // Create acronym from company name (first letter of each word)
    const companyAcronym = companyLower.split(/\s+/).map(w => w[0]).join('');
    
    // Create variations of company name for matching
    const companyVariations = [
      companyLower,
      companyLower.replace(/\s+/g, ''), // Remove spaces
      companyLower.replace(/\s+/g, '-'), // Replace spaces with dash
      companyLower.replace(/[^a-z0-9]/g, ''), // Remove special characters
      companyLower.split(' ')[0], // First word only (e.g., "Google" from "Google Inc")
      companyAcronym, // Acronym (e.g., "tcs" from "Tata Consultancy Services")
    ];
    
    // Add common company name variations
    const commonAbbreviations = {
      'limited': 'ltd',
      'private': 'pvt',
      'corporation': 'corp',
      'incorporated': 'inc',
      'technologies': 'tech',
      'consultancy': 'consulting',
      'services': 'svc',
    };
    
    let expandedVariations = [...companyVariations];
    Object.entries(commonAbbreviations).forEach(([full, abbr]) => {
      if (companyLower.includes(full)) {
        expandedVariations.push(companyLower.replace(full, abbr).replace(/\s+/g, ''));
      }
    });
    
    // Also check if domain contains company name or vice versa
    const domainVariations = [
      emailDomain,
      emailDomain.replace(/-/g, ''),
      emailDomain.replace(/\d+/g, ''), // Remove numbers
    ];
    
    // Check if any company variation matches any domain variation
    for (const cv of expandedVariations) {
      for (const dv of domainVariations) {
        if (cv && dv && cv.length > 1 && dv.length > 1) {
          // Exact match
          if (cv === dv) {
            setEmailDomainError(null);
            return true;
          }
          // Check if one contains the other (for longer strings)
          if (cv.length > 2 && dv.length > 2) {
            if (cv.includes(dv) || dv.includes(cv)) {
              setEmailDomainError(null);
              return true;
            }
          }
          // Check similarity (at least 70% match for similar length strings)
          const minLen = Math.min(cv.length, dv.length);
          const maxLen = Math.max(cv.length, dv.length);
          if (maxLen <= 8 || minLen >= 3) { // Only for reasonably sized strings
            let matches = 0;
            for (let i = 0; i < minLen; i++) {
              if (cv[i] === dv[i]) matches++;
            }
            if (matches / maxLen >= 0.7) {
              setEmailDomainError(null);
              return true;
            }
          }
        }
      }
    }
    
    setEmailDomainError(`Company name "${companyName}" doesn't match email domain "@${emailParts[1]}"`);
    return false;
  };
  
  // Check email domain whenever company or email changes
  // Only validate when email looks complete (has @ and domain with extension)
  useEffect(() => {
    if (formData.company_email) {
      // Only validate if email has @ and looks complete (has domain extension)
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(formData.company_email)) {
        validateCompanyEmailDomain(formData.company, formData.company_email);
        
        // Also check if email contains user's name
        if (!validateEmailContainsName(formData.company_email)) {
          const { firstName, lastName } = getUserNameParts();
          setEmailNameError(`Email should contain your name (${firstName || ''} or ${lastName || ''})`);
        } else {
          setEmailNameError(null);
        }
      } else {
        // Email is incomplete - clear any previous error
        setEmailDomainError(null);
        setEmailNameError(null);
      }
    } else {
      setEmailDomainError(null);
      setEmailNameError(null);
    }
  }, [formData.company, formData.company_email, user]);
  
  // Check LinkedIn URL contains user's name when it changes
  useEffect(() => {
    if (formData.linkedin_url && user?.name) {
      // Only validate if URL looks complete
      if (formData.linkedin_url.includes('linkedin.com/in/')) {
        if (!validateLinkedInContainsName(formData.linkedin_url)) {
          const { firstName, lastName } = getUserNameParts();
          setLinkedinNameError(`LinkedIn URL should contain your name (${firstName || ''} or ${lastName || ''})`);
        } else {
          setLinkedinNameError(null);
        }
      } else {
        setLinkedinNameError(null);
      }
    } else {
      setLinkedinNameError(null);
    }
  }, [formData.linkedin_url, user]);
  
  // Auto-populate company name from email domain
  const extractCompanyFromEmail = (email) => {
    if (!email || !email.includes('@')) return null;
    const domain = email.split('@')[1];
    if (!domain) return null;
    const companyPart = domain.split('.')[0];
    // Capitalize first letter
    return companyPart.charAt(0).toUpperCase() + companyPart.slice(1);
  };
  
  // Handle company email change and auto-populate company
  const handleCompanyEmailChange = (email) => {
    setFormData(prev => ({ ...prev, company_email: email }));
    
    // Auto-populate company if not already set
    if (email && email.includes('@') && !formData.company) {
      const extractedCompany = extractCompanyFromEmail(email);
      if (extractedCompany) {
        setFormData(prev => ({ ...prev, company: extractedCompany }));
        toast.success(`Company set to "${extractedCompany}" based on email`);
      }
    }
  };
  
  // Check if OTP can be sent (email domain must match)
  const canSendOTP = formData.company_email && !emailDomainError && placementId;
  
  // Calculate verification status 
  const isFullyVerified = verificationState.linkedin_verified && verificationState.company_email_verified;
  
  // Function to check if company names match (case-insensitive, fuzzy match)
  const checkCompanyNameMatch = (userCompany, offerCompany) => {
    if (!userCompany || !offerCompany) return true; // No mismatch if one is missing
    
    const normalizeCompany = (name) => {
      return name.toLowerCase()
        .replace(/\s+/g, '') // Remove spaces
        .replace(/[.,\-_&]/g, '') // Remove punctuation
        .replace(/private|limited|pvt|ltd|inc|incorporated|llp|llc|corp|corporation/gi, '') // Remove common suffixes
        .trim();
    };
    
    const userNormalized = normalizeCompany(userCompany);
    const offerNormalized = normalizeCompany(offerCompany);
    
    // Exact match after normalization
    if (userNormalized === offerNormalized) return true;
    
    // Check if one contains the other
    if (userNormalized.includes(offerNormalized) || offerNormalized.includes(userNormalized)) return true;
    
    // Check if first word matches (e.g., "Google" matches "Google India")
    const userFirstWord = userCompany.toLowerCase().split(/\s+/)[0];
    const offerFirstWord = offerCompany.toLowerCase().split(/\s+/)[0];
    if (userFirstWord === offerFirstWord && userFirstWord.length > 3) return true;
    
    return false;
  };
  
  // Check company match whenever company or offerDetails changes
  useEffect(() => {
    if (offerDetails?.company_name && formData.company) {
      const isMatch = checkCompanyNameMatch(formData.company, offerDetails.company_name);
      if (!isMatch) {
        setCompanyMismatchError(`Company name mismatch: You entered "${formData.company}" but offer letter shows "${offerDetails.company_name}"`);
      } else {
        setCompanyMismatchError(null);
      }
    } else {
      setCompanyMismatchError(null);
    }
  }, [formData.company, offerDetails]);
  
  // Trigger confetti when fully verified
  useEffect(() => {
    if (isFullyVerified) {
      triggerSuccessConfetti();
    }
  }, [isFullyVerified]);

  // Search colleges as user types
  useEffect(() => {
    const searchColleges = async () => {
      if (collegeSearchQuery.length < 2) {
        setCollegeSuggestions([]);
        return;
      }
      
      setLoadingColleges(true);
      try {
        const res = await axios.get(`${API}/colleges/search?q=${encodeURIComponent(collegeSearchQuery)}&limit=10`);
        setCollegeSuggestions(res.data);
      } catch (error) {
        console.error("Error searching colleges:", error);
      } finally {
        setLoadingColleges(false);
      }
    };
    
    const debounce = setTimeout(searchColleges, 300);
    return () => clearTimeout(debounce);
  }, [collegeSearchQuery]);
  
  // Fetch programs when college is selected
  useEffect(() => {
    const fetchPrograms = async () => {
      if (!formData.college_name) {
        setCollegePrograms([]);
        return;
      }
      
      setLoadingPrograms(true);
      try {
        const res = await axios.get(`${API}/colleges/programs/${encodeURIComponent(formData.college_name)}`);
        setCollegePrograms(res.data);
      } catch (error) {
        console.error("Error fetching programs:", error);
        setCollegePrograms([]);
      } finally {
        setLoadingPrograms(false);
      }
    };
    
    fetchPrograms();
  }, [formData.college_name]);
  
  // Handle college selection from autocomplete
  const handleCollegeSelect = (college) => {
    setFormData(prev => ({
      ...prev,
      college_id: college.id,
      college_name: college.name,
      program_id: "" // Reset program when college changes
    }));
    setCollegeSearchQuery(college.name);
    setShowCollegeSuggestions(false);
  };
  
  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (collegeInputRef.current && !collegeInputRef.current.contains(e.target)) {
        setShowCollegeSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    // Just set loading to false - colleges are now fetched via search
    setLoading(false);
  }, []);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const calculateTotal = () => {
    if (formData.placement_type === "internship") {
      const stipend = parseFloat(formData.internship_stipend) || 0;
      const duration = parseInt(formData.internship_duration) || 1;
      return stipend * duration;
    }
    const fixed = parseFloat(formData.fixed) || 0;
    const variable = parseFloat(formData.variable) || 0;
    const esops = parseFloat(formData.esops) || 0;
    const joining_bonus = parseFloat(formData.joining_bonus) || 0;
    const relocation = parseFloat(formData.relocation) || 0;
    return fixed + variable + esops + joining_bonus + relocation;
  };

  // Verify CTC against market data and peer corroboration
  const verifyCTC = async () => {
    const totalCtc = calculateTotal();
    if (!totalCtc || !formData.company || !formData.role) {
      setCtcVerification(null);
      return;
    }
    
    setVerifyingCtc(true);
    try {
      const res = await axios.post(`${API}/salary/verify`, {
        ctc: totalCtc,
        role: formData.role,
        company: formData.company,
        experience_years: 0
      });
      setCtcVerification(res.data);
    } catch (error) {
      console.error("CTC verification failed:", error);
      setCtcVerification(null);
    } finally {
      setVerifyingCtc(false);
    }
  };

  // Auto-verify CTC when salary or company/role changes (debounced)
  useEffect(() => {
    const totalCtc = calculateTotal();
    if (totalCtc > 0 && formData.company && formData.role && !salaryAutoFilled) {
      const debounce = setTimeout(() => {
        verifyCTC();
      }, 800);
      return () => clearTimeout(debounce);
    }
  }, [formData.fixed, formData.variable, formData.esops, formData.joining_bonus, formData.relocation, formData.company, formData.role]);

  const validateStep = (stepNum) => {
    switch (stepNum) {
      case 1:
        return formData.college_name && formData.program_id;
      case 2:
        return formData.company && formData.role && formData.year;
      case 3:
        // Must choose a verification method
        if (!verificationMethod) return false;
        
        // Block if company name doesn't match offer letter
        if (companyMismatchError) return false;
        
        if (formData.placement_type === "internship") {
          const hasStipend = parseFloat(formData.internship_stipend) > 0;
          if (verificationMethod === 'offer_letter') {
            return hasStipend && !!offerLetterUrl;
          }
          return hasStipend;
        }
        
        // For placements
        const hasFixed = parseFloat(formData.fixed) > 0;
        
        if (verificationMethod === 'offer_letter') {
          // Only need fixed salary + offer letter for verified submission
          return hasFixed && !!offerLetterUrl;
        }
        
        // Manual entry - all fields required
        const hasVariable = formData.variable !== "" && parseFloat(formData.variable) >= 0;
        const hasEsops = formData.esops !== "" && parseFloat(formData.esops) >= 0;
        const hasJoiningBonus = formData.joining_bonus !== "" && parseFloat(formData.joining_bonus) >= 0;
        const hasRelocation = formData.relocation !== "" && parseFloat(formData.relocation) >= 0;
        
        return hasFixed && hasVariable && hasEsops && hasJoiningBonus && hasRelocation;
      case 4:
        // Block if company email is provided but doesn't match company name
        if (formData.company_email && emailDomainError) {
          return false;
        }
        // Block if company email doesn't contain user's name
        if (formData.company_email && emailNameError) {
          return false;
        }
        // Block if LinkedIn URL doesn't contain user's name
        if (formData.linkedin_url && linkedinNameError) {
          return false;
        }
        // Block if company name doesn't match offer letter
        if (companyMismatchError) return false;
        return formData.linkedin_url && (formData.company_email || formData.college_email);
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    } else {
      if (companyMismatchError) {
        toast.error("Company name doesn't match your offer letter");
      } else {
        toast.error("Please fill all required fields");
      }
    }
  };

  // Submit placement (initial submission)
  const handleSubmitPlacement = async () => {
    if (!validateStep(4)) {
      toast.error("Please provide LinkedIn URL and at least one email for verification");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        college_id: formData.college_name, // Using college_name as ID for new data
        college_name: formData.college_name,
        program_id: formData.program_id,
        program_name: formData.program_id, // Program name is the same as ID for new data
        company: formData.company,
        role: formData.role,
        fixed: parseFloat(formData.fixed) || 0,
        variable: parseFloat(formData.variable) || 0,
        esops: parseFloat(formData.esops) || 0,
        joining_bonus: parseFloat(formData.joining_bonus) || 0,
        relocation: parseFloat(formData.relocation) || 0,
        year: parseInt(formData.year),
        placement_type: formData.placement_type,
        is_ppo: formData.is_ppo,
        internship_stipend: formData.placement_type === "internship" ? parseFloat(formData.internship_stipend) || 0 : null,
        internship_duration: formData.placement_type === "internship" ? parseInt(formData.internship_duration) || 2 : null,
        linkedin_url: formData.linkedin_url,
        college_email: formData.college_email || null,
        company_email: formData.company_email || null,
        verification_method: verificationMethod,
        // If offer letter method, mark as verified (letter is processed but not stored)
        initial_verification_status: verificationMethod === 'offer_letter' ? 'verified' : 'pending'
      };

      const response = await axios.post(`${API}/placements`, payload);
      setPlacementId(response.data.placement_id);
      toast.success("Placement submitted! Now let's verify your details.");
      setStep(5); // Move to verification step
    } catch (error) {
      console.error("Submission error:", error);
      toast.error(error.response?.data?.detail || "Failed to submit placement");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle offer letter upload - PDF ONLY
  const handleOfferLetterUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    console.log("[Upload] Starting offer letter upload...");
    console.log("[Upload] File:", file.name, file.type, file.size);
    
    // Validate file type - PDF ONLY
    if (file.type !== 'application/pdf') {
      toast.error("Only PDF files are accepted");
      e.target.value = ''; // Reset input
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      e.target.value = '';
      return;
    }
    
    // User is authenticated if they can see this form (checked in render)
    // The axios interceptor will automatically add the auth token
    console.log("[Upload] Proceeding with upload...");
    
    setOfferLetterFile(file);
    setUploadingOfferLetter(true);
    setExtractedSalary(null);
    setSalaryBreakdown(null);
    setOfferDetails(null);
    setFraudAnalysis(null);
    setSalaryAutoFilled(false);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      console.log("[Upload] Sending request to backend...");
      console.log("[Upload] API URL:", API);
      
      const response = await axios.post(`${API}/upload/offer-letter`, formDataUpload, {
        // Don't use withCredentials - we use Bearer token auth, not cookies
        timeout: 120000, // 2 minute timeout for AI analysis (iOS can be slower)
        // iOS Safari specific: don't cache this request
        params: { _t: Date.now() }
      });
      
      console.log("[Upload] Response received:", response.status);
      
      const data = response.data;
      setOfferLetterUrl(data.file_url);
      
      // Store full salary breakdown
      if (data.salary_breakdown) {
        setSalaryBreakdown(data.salary_breakdown);
      }
      
      // Store offer details
      if (data.offer_details) {
        setOfferDetails(data.offer_details);
      }
      
      // Store fraud analysis
      if (data.fraud_analysis) {
        setFraudAnalysis(data.fraud_analysis);
      }
      
      // Auto-fill salary from extracted data
      if (data.extracted_salary) {
        setExtractedSalary(data.extracted_salary);
        
        // Auto-fill the form with extracted salary data
        const salary = data.extracted_salary;
        setFormData(prev => ({
          ...prev,
          fixed: salary.fixed_salary ? Math.round(salary.fixed_salary).toString() : prev.fixed,
          variable: salary.variable_pay ? Math.round(salary.variable_pay).toString() : "0",
          esops: salary.esops ? Math.round(salary.esops).toString() : "0",
          joining_bonus: salary.joining_bonus ? Math.round(salary.joining_bonus).toString() : "0",
          relocation: salary.relocation_allowance ? Math.round(salary.relocation_allowance).toString() : "0",
          // If we have total_ctc but not fixed, use total_ctc as fixed
          ...(salary.total_ctc && !salary.fixed_salary ? { fixed: Math.round(salary.total_ctc).toString() } : {})
        }));
        setSalaryAutoFilled(true);
        
        // Also auto-fill company and role if extracted from offer details
        if (data.offer_details) {
          const details = data.offer_details;
          if (details.company_name && !formData.company) {
            setFormData(prev => ({ ...prev, company: details.company_name }));
          }
          if (details.role && !formData.role) {
            setFormData(prev => ({ ...prev, role: details.role }));
          }
        }
      }
      
      // Show appropriate toast based on authenticity
      if (data.is_likely_authentic) {
        toast.success(`Offer letter verified! Authenticity: ${data.authenticity_score}%`);
      } else if (data.authenticity_score >= 50) {
        toast.success(`Offer letter uploaded successfully! Score: ${data.authenticity_score}%`);
      } else if (data.authenticity_score > 0) {
        toast.info(`Document processed. Authenticity score: ${data.authenticity_score}%. Your submission will be reviewed.`);
      } else {
        toast.info(`Document uploaded. AI analysis unavailable - your submission will be manually reviewed.`);
      }
      
      // Show fraud warnings only if score is very low
      if (data.fraud_analysis?.red_flags?.length > 3 && data.authenticity_score < 50) {
        toast.warning(`Note: ${data.fraud_analysis.red_flags.length} potential issue(s) detected`);
      }
      
    } catch (error) {
      console.error("Upload error:", error);
      console.error("Error response:", error.response?.data);
      console.error("Error status:", error.response?.status);
      
      // More specific error messages
      let errorMsg = "Failed to process offer letter";
      
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        errorMsg = "Upload timed out. Please try again with a smaller file.";
      } else if (error.response?.status === 401) {
        // Session expired or not authenticated
        errorMsg = "Your session has expired. Please refresh the page and sign in again.";
        // Clear stale token
        localStorage.removeItem('transplacement_token');
      } else if (error.response?.status === 400) {
        errorMsg = error.response?.data?.detail || "Invalid file format. Please upload a PDF.";
      } else if (error.response?.status === 413) {
        errorMsg = "File too large. Please upload a smaller file (max 5MB).";
      } else if (error.response?.data?.detail) {
        errorMsg = error.response.data.detail;
      } else if (!navigator.onLine) {
        errorMsg = "No internet connection. Please check your network.";
      } else if (error.message?.includes('Network Error')) {
        errorMsg = "Network error. Please check your connection and try again.";
      }
      
      toast.error(errorMsg);
      setOfferLetterFile(null);
      setExtractedSalary(null);
      setSalaryBreakdown(null);
      setOfferDetails(null);
      setFraudAnalysis(null);
    } finally {
      setUploadingOfferLetter(false);
    }
  };

  // Verify LinkedIn - Auto verify when user opens profile
  const handleOpenLinkedInProfile = async () => {
    // Validate LinkedIn URL format
    if (!formData.linkedin_url) {
      toast.error("Please enter your LinkedIn profile URL");
      return;
    }
    
    // Normalize LinkedIn URL
    let linkedinUrl = formData.linkedin_url.trim();
    if (!linkedinUrl.startsWith('http://') && !linkedinUrl.startsWith('https://')) {
      linkedinUrl = 'https://' + linkedinUrl;
    }
    
    // Validate URL format
    if (!linkedinUrl.includes('linkedin.com/in/')) {
      toast.error("Please enter a valid LinkedIn profile URL (e.g., linkedin.com/in/your-name)");
      setVerificationState(prev => ({ 
        ...prev, 
        linkedin_error: "Invalid LinkedIn URL format. Use: linkedin.com/in/your-profile" 
      }));
      return;
    }
    
    if (!placementId) {
      toast.error("Please complete previous steps first");
      return;
    }
    
    // Validate that LinkedIn URL contains user's name
    if (!validateLinkedInContainsName(linkedinUrl)) {
      const { firstName, lastName } = getUserNameParts();
      const errorMsg = `LinkedIn URL should contain your name (${firstName || ''} or ${lastName || ''})`;
      setLinkedinNameError(errorMsg);
      setVerificationState(prev => ({ ...prev, linkedin_error: errorMsg }));
      toast.error(errorMsg);
      return;
    }
    
    setLinkedinNameError(null);
    
    // Start verification process
    setVerificationState(prev => ({ ...prev, verifying_linkedin: true, linkedin_error: null }));
    
    // Open LinkedIn profile in new tab
    const newWindow = window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
    
    // Small delay to ensure window opens before API call
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const response = await axios.post(`${API}/verification/verify-linkedin`, {
        linkedin_url: linkedinUrl,
        company: formData.company,
        role: formData.role,
        placement_id: placementId,
        user_confirmed: true
      });
      
      if (response.data.verified) {
        setVerificationState(prev => ({ 
          ...prev, 
          linkedin_verified: true, 
          linkedin_error: null,
          company_verified: true,
          role_verified: true,
          verifying_linkedin: false
        }));
        
        toast.success("LinkedIn profile verified successfully!");
      } else {
        throw new Error(response.data.message || "Verification failed");
      }
      
    } catch (error) {
      console.error("LinkedIn verification error:", error);
      const errorMsg = error.response?.data?.detail || error.message || "Failed to verify LinkedIn";
      setVerificationState(prev => ({ 
        ...prev, 
        linkedin_error: errorMsg,
        verifying_linkedin: false 
      }));
      toast.error(errorMsg);
    }
  };

  // Send Company Email OTP
  const handleSendCompanyOTP = async () => {
    if (!placementId || !formData.company_email) return;
    
    setVerificationState(prev => ({ ...prev, sending_otp: true }));
    try {
      await axios.post(`${API}/verification/send-otp`, {
        email: formData.company_email,
        email_type: "company_email",
        placement_id: placementId
      });
      
      setVerificationState(prev => ({ ...prev, company_otp_sent: true }));
      toast.success(`OTP sent to ${formData.company_email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setVerificationState(prev => ({ ...prev, sending_otp: false }));
    }
  };

  // Verify Company Email OTP
  const handleVerifyCompanyOTP = async () => {
    if (!placementId || !verificationState.company_otp) return;
    
    setVerificationState(prev => ({ ...prev, verifying_company_otp: true }));
    try {
      const response = await axios.post(`${API}/verification/verify-otp`, {
        email: formData.company_email,
        otp: verificationState.company_otp,
        placement_id: placementId,
        email_type: "company_email"
      });
      
      if (response.data.verified) {
        setVerificationState(prev => ({ ...prev, company_email_verified: true }));
        toast.success("Company email verified!");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid OTP");
    } finally {
      setVerificationState(prev => ({ ...prev, verifying_company_otp: false }));
    }
  };

  // Redirect to login if not authenticated
  // Show loading state during auth check AND session creation
  if (authLoading || sessionLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        <p className="text-sm text-muted-foreground">
          {sessionLoading ? "Completing sign in..." : "Loading..."}
        </p>
      </div>
    );
  }
  
  if (!user) {
    return (
      <div className="min-h-screen">
        <nav className="border-b border-border bg-background sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="font-heading text-xl font-semibold tracking-tight">
                Transplacement
              </Link>
            </div>
          </div>
        </nav>
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">
            Sign In Required
          </h1>
          <p className="text-muted-foreground mt-4">
            Please sign in to submit your placement details.
          </p>
          <Button onClick={() => login('/submit')} className="mt-8 h-12 px-8 font-medium" data-testid="login-btn">
            Sign In with Google
          </Button>
        </div>
      </div>
    );
  }

  const StepIndicator = () => (
    <div className="step-indicator mb-8 justify-center">
      {[1, 2, 3, 4, 5].map((s, i) => (
        <div key={s} className="flex items-center">
          <div className={`step-dot ${step === s ? 'active' : step > s ? 'completed' : 'inactive'}`}>
            {step > s ? <Check className="h-4 w-4" /> : s}
          </div>
          {i < 4 && <div className={`step-line mx-2 w-8 md:w-12 ${step > s ? 'completed' : ''}`} />}
        </div>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="border-b border-border bg-background sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <Link to="/" className="font-heading text-lg sm:text-xl font-semibold tracking-tight" data-testid="logo">
              Transplacement
            </Link>
            
            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/dashboard" className="text-sm font-medium hover:text-primary/80">
                Rankings
              </Link>
              <Link to="/profile" data-testid="nav-profile">
                <Button variant="outline" className="h-10 px-4 text-sm font-medium rounded-full">
                  Profile
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button 
              className="md:hidden p-2 -mr-2 touch-target"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 bg-background z-40 md:hidden">
          <div className="flex flex-col p-6 pt-20">
            <button 
              onClick={() => handleMobileNav('/')}
              className="py-4 text-lg font-medium border-b border-border text-left"
            >
              Home
            </button>
            <button 
              onClick={() => handleMobileNav('/dashboard')}
              className="py-4 text-lg font-medium border-b border-border text-left"
            >
              Rankings
            </button>
            <button 
              onClick={() => handleMobileNav('/profile')}
              className="py-4 text-lg font-medium border-b border-border text-left"
            >
              My Profile
            </button>
          </div>
        </div>
      )}

      {/* Back Button */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 pt-3 sm:pt-4">
        <button 
          onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)} 
          className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground hover:text-foreground transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="h-4 w-4" />
          {step > 1 ? 'Previous Step' : 'Back'}
        </button>
      </div>

      {/* Form */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h1 className="font-heading text-xl sm:text-2xl md:text-3xl font-bold uppercase tracking-tight text-center">
          Submit Your Placement
        </h1>
        <p className="text-muted-foreground text-center mt-2 mb-6 sm:mb-8 text-sm sm:text-base">
          {step < 5 ? "Your submission is anonymous. Only aggregate data is shown publicly." : "Verify your details to get your submission approved."}
        </p>

        <div className="flex justify-center mb-6 sm:mb-8">
          <StepIndicator />
        </div>

        {loading ? (
          <div className="space-y-4">
            <div className="skeleton h-12 w-full" />
            <div className="skeleton h-12 w-full" />
            <div className="skeleton h-12 w-full" />
          </div>
        ) : (
          <div className="border border-border p-4 sm:p-6 md:p-8">
            {/* Step 1: College & Program */}
            {step === 1 && (
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 bg-primary text-primary-foreground flex items-center justify-center">
                    <Building2 className="h-4 w-4 sm:h-5 sm:w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-base sm:text-lg font-bold uppercase">College & Program</h2>
                    <p className="text-xs sm:text-sm text-muted-foreground">Select your college and program</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div ref={collegeInputRef} className="relative">
                    <Label>College / Institute *</Label>
                    <Input
                      placeholder="Start typing college name..."
                      value={collegeSearchQuery}
                      onChange={(e) => {
                        setCollegeSearchQuery(e.target.value);
                        setShowCollegeSuggestions(true);
                        if (e.target.value !== formData.college_name) {
                          setFormData(prev => ({ ...prev, college_id: "", college_name: "", program_id: "" }));
                        }
                      }}
                      onFocus={() => collegeSearchQuery.length >= 2 && setShowCollegeSuggestions(true)}
                      className="h-12 mt-2"
                      data-testid="college-input"
                    />
                    {showCollegeSuggestions && (collegeSearchQuery.length >= 2) && (
                      <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                        {loadingColleges ? (
                          <div className="p-3 text-center text-muted-foreground">
                            <Loader2 className="h-4 w-4 animate-spin inline mr-2" />
                            Searching...
                          </div>
                        ) : collegeSuggestions.length > 0 ? (
                          collegeSuggestions.map((college) => (
                            <button
                              key={college.id}
                              onClick={() => handleCollegeSelect(college)}
                              className="w-full text-left px-4 py-3 hover:bg-secondary transition-colors border-b border-border last:border-b-0"
                              data-testid={`college-option-${college.id}`}
                            >
                              <span className="font-medium">{college.name}</span>
                            </button>
                          ))
                        ) : (
                          <div className="p-3 text-center text-muted-foreground">
                            No colleges found
                          </div>
                        )}
                      </div>
                    )}
                    {formData.college_name && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-green-600">
                        <CheckCircle className="h-4 w-4" />
                        Selected: {formData.college_name}
                      </div>
                    )}
                  </div>

                  <div>
                    <Label>Program *</Label>
                    <Select 
                      value={formData.program_id} 
                      onValueChange={(v) => handleInputChange('program_id', v)}
                      disabled={!formData.college_name || loadingPrograms}
                    >
                      <SelectTrigger className="h-12 mt-2" data-testid="program-select">
                        <SelectValue placeholder={loadingPrograms ? "Loading programs..." : "Select your program"} />
                      </SelectTrigger>
                      <SelectContent>
                        {collegePrograms.map(prog => (
                          <SelectItem key={prog.id} value={prog.id}>
                            {prog.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!formData.college_name && (
                      <p className="text-xs text-muted-foreground mt-1">Select a college first to see available programs</p>
                    )}
                  </div>

                  <div>
                    <Label>Submission Type *</Label>
                    <Select 
                      value={formData.placement_type} 
                      onValueChange={(v) => handleInputChange('placement_type', v)}
                    >
                      <SelectTrigger className="h-12 mt-2" data-testid="type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="placement">Full-Time Placement</SelectItem>
                        <SelectItem value="internship">Internship</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Offer Details */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center">
                    <Briefcase className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold uppercase">Offer Details</h2>
                    <p className="text-sm text-muted-foreground">Enter your company and role</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label>Company Name *</Label>
                    <Input
                      placeholder="e.g., Google, Microsoft, TCS"
                      value={formData.company}
                      onChange={(e) => handleInputChange('company', e.target.value)}
                      className="h-12 mt-2"
                      data-testid="company-input"
                    />
                  </div>

                  <div>
                    <Label>Role / Designation *</Label>
                    <Input
                      placeholder="e.g., Software Engineer, Analyst"
                      value={formData.role}
                      onChange={(e) => handleInputChange('role', e.target.value)}
                      className="h-12 mt-2"
                      data-testid="role-input"
                    />
                  </div>

                  <div>
                    <Label>{formData.placement_type === "internship" ? "Internship" : "Placement"} Year *</Label>
                    <Select 
                      value={formData.year} 
                      onValueChange={(v) => handleInputChange('year', v)}
                    >
                      <SelectTrigger className="h-12 mt-2" data-testid="year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2025, 2024, 2023, 2022, 2021].map(year => (
                          <SelectItem key={year} value={year.toString()}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {formData.placement_type === "placement" && (
                    <div className="flex items-center gap-3 mt-4">
                      <input
                        type="checkbox"
                        id="is_ppo"
                        checked={formData.is_ppo}
                        onChange={(e) => handleInputChange('is_ppo', e.target.checked)}
                        className="h-5 w-5"
                        data-testid="ppo-checkbox"
                      />
                      <Label htmlFor="is_ppo" className="cursor-pointer">
                        This was a PPO (Pre-Placement Offer after internship)
                      </Label>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 3: Salary/Stipend Breakdown */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center">
                    <DollarSign className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold uppercase">
                      {formData.placement_type === "internship" ? "Stipend Details" : "Salary Breakdown"}
                    </h2>
                    <p className="text-sm text-muted-foreground">
                      Choose how you want to submit your {formData.placement_type === "internship" ? "stipend" : "salary"} details
                    </p>
                  </div>
                </div>

                {/* Privacy Notice */}
                <div className="p-4 bg-blue-50 border border-blue-200 text-blue-800 text-sm">
                  <p className="font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4" />
                    Your Privacy is Protected
                  </p>
                  <p className="mt-1 text-blue-700">
                    We will <strong>NOT</strong> save your offer letter. Your name will <strong>NEVER</strong> be shown or saved in our database. All submissions are completely anonymous.
                  </p>
                </div>

                {/* Verification Method Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Option 1: Manual Entry */}
                  <div 
                    onClick={() => setVerificationMethod('manual')}
                    className={`p-6 border-2 cursor-pointer transition-all ${
                      verificationMethod === 'manual' 
                        ? 'border-primary bg-primary/5' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    data-testid="manual-entry-option"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        verificationMethod === 'manual' ? 'border-primary bg-primary' : 'border-muted-foreground'
                      }`}>
                        {verificationMethod === 'manual' && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <h3 className="font-heading text-base font-bold uppercase">Type in your details</h3>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs font-medium uppercase">
                        Unverified
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Manually enter your salary details. Your submission will appear as unverified until admin review.
                    </p>
                  </div>

                  {/* Option 2: Upload Offer Letter */}
                  <div 
                    onClick={() => setVerificationMethod('offer_letter')}
                    className={`p-6 border-2 cursor-pointer transition-all ${
                      verificationMethod === 'offer_letter' 
                        ? 'border-green-500 bg-green-50' 
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    data-testid="offer-letter-option"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        verificationMethod === 'offer_letter' ? 'border-green-500 bg-green-500' : 'border-muted-foreground'
                      }`}>
                        {verificationMethod === 'offer_letter' && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <h3 className="font-heading text-base font-bold uppercase">Upload Offer Letter</h3>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium uppercase">
                        Verified
                      </span>
                      <span className="text-xs text-green-600">Recommended</span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Upload your offer letter for instant verification. We extract data and immediately delete the file.
                    </p>
                  </div>
                </div>

                {/* Form based on selected method */}
                {verificationMethod && (
                  <div className="mt-6 pt-6 border-t border-border">
                    {verificationMethod === 'offer_letter' ? (
                      /* Offer Letter Upload Section */
                      <div className="space-y-4">
                        <h4 className="font-medium">Upload your offer letter</h4>
                        
                        {/* Offer Letter Upload */}
                        <div className="p-4 border border-dashed border-border">
                          {offerLetterUrl ? (
                            <div className="space-y-4">
                              <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <CheckCircle className="h-6 w-6 text-green-600" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-green-800">Offer letter analyzed</p>
                                  <p className="text-xs text-green-600 truncate">{offerLetterFile?.name}</p>
                                  <p className="text-xs text-green-600 mt-1">File processed and discarded for your privacy</p>
                                </div>
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  onClick={() => { 
                                    setOfferLetterFile(null); 
                                    setOfferLetterUrl(null); 
                                    setExtractedSalary(null);
                                    setSalaryBreakdown(null);
                                    setOfferDetails(null);
                                    setFraudAnalysis(null);
                                    setSalaryAutoFilled(false);
                                    setFormData(prev => ({
                                      ...prev,
                                      fixed: "", variable: "", esops: "", joining_bonus: "", relocation: ""
                                    }));
                                  }}
                                  className="text-green-700 hover:text-green-800 hover:bg-green-100"
                                >
                                  <XCircle className="h-4 w-4" />
                                </Button>
                              </div>
                              
                              {/* Fraud Analysis Results */}
                              {fraudAnalysis && (
                                <div className="space-y-4">
                                  {/* Main Status Card */}
                                  <div className={`p-5 rounded-2xl border-2 ${
                                    fraudAnalysis.authenticity_score >= 70 
                                      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-800' 
                                      : fraudAnalysis.authenticity_score >= 40 
                                        ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800' 
                                        : 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-800'
                                  }`}>
                                    {/* Header with Score */}
                                    <div className="flex items-center justify-between mb-4">
                                      <div className="flex items-center gap-3">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                                          fraudAnalysis.authenticity_score >= 70 
                                            ? 'bg-emerald-500' 
                                            : fraudAnalysis.authenticity_score >= 40 
                                              ? 'bg-amber-500' 
                                              : 'bg-red-500'
                                        }`}>
                                          {fraudAnalysis.authenticity_score >= 70 ? (
                                            <CheckCircle className="h-6 w-6 text-white" />
                                          ) : fraudAnalysis.authenticity_score >= 40 ? (
                                            <Shield className="h-6 w-6 text-white" />
                                          ) : (
                                            <XCircle className="h-6 w-6 text-white" />
                                          )}
                                        </div>
                                        <div>
                                          <h4 className={`font-semibold ${
                                            fraudAnalysis.authenticity_score >= 70 
                                              ? 'text-emerald-800 dark:text-emerald-200' 
                                              : fraudAnalysis.authenticity_score >= 40 
                                                ? 'text-amber-800 dark:text-amber-200' 
                                                : 'text-red-800 dark:text-red-200'
                                          }`}>
                                            {fraudAnalysis.authenticity_score >= 70 
                                              ? 'Document Verified' 
                                              : fraudAnalysis.authenticity_score >= 40 
                                                ? 'Needs Review' 
                                                : 'Verification Failed'}
                                          </h4>
                                          <p className="text-xs text-muted-foreground">AI-powered authenticity check</p>
                                        </div>
                                      </div>
                                      <div className={`px-4 py-2 rounded-xl text-lg font-bold ${
                                        fraudAnalysis.authenticity_score >= 70 
                                          ? 'bg-emerald-500 text-white' 
                                          : fraudAnalysis.authenticity_score >= 40 
                                            ? 'bg-amber-500 text-white' 
                                            : 'bg-red-500 text-white'
                                      }`}>
                                        {fraudAnalysis.authenticity_score}%
                                      </div>
                                    </div>
                                    
                                    {/* Red Flags */}
                                    {fraudAnalysis.red_flags?.length > 0 && (
                                      <div className="mt-4 pt-4 border-t border-red-200 dark:border-red-800">
                                        <p className="text-xs font-semibold text-red-700 dark:text-red-300 uppercase tracking-wide mb-2">Issues Detected</p>
                                        <div className="space-y-1.5">
                                          {fraudAnalysis.red_flags.map((flag, i) => (
                                            <div key={i} className="flex items-start gap-2 text-sm text-red-700 dark:text-red-300">
                                              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" />
                                              <span>{flag}</span>
                                            </div>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* AI Notes */}
                                    {fraudAnalysis.ai_fraud_notes && (
                                      <p className="mt-3 text-xs text-muted-foreground italic bg-white/50 dark:bg-black/20 p-2 rounded-lg">
                                        {fraudAnalysis.ai_fraud_notes}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Extracted Offer Details */}
                              {offerDetails && (offerDetails.company_name || offerDetails.role || offerDetails.joining_date) && (
                                <div className="p-5 bg-secondary/50 dark:bg-secondary/30 border border-border rounded-2xl">
                                  <div className="flex items-center gap-2 mb-4">
                                    <FileText className="h-5 w-5 text-foreground/70" />
                                    <h5 className="font-semibold text-sm">Extracted Details</h5>
                                  </div>
                                  <div className="grid grid-cols-2 gap-4">
                                    {offerDetails.company_name && (
                                      <div className="bg-background/80 p-3 rounded-xl">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Company</p>
                                        <p className="font-medium text-sm">{offerDetails.company_name}</p>
                                      </div>
                                    )}
                                    {offerDetails.role && (
                                      <div className="bg-background/80 p-3 rounded-xl">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Role</p>
                                        <p className="font-medium text-sm">{offerDetails.role}</p>
                                      </div>
                                    )}
                                    {offerDetails.department && (
                                      <div className="bg-background/80 p-3 rounded-xl">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Department</p>
                                        <p className="font-medium text-sm">{offerDetails.department}</p>
                                      </div>
                                    )}
                                    {offerDetails.joining_date && (
                                      <div className="bg-background/80 p-3 rounded-xl">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Joining Date</p>
                                        <p className="font-medium text-sm">{offerDetails.joining_date}</p>
                                      </div>
                                    )}
                                    {offerDetails.work_location && (
                                      <div className="bg-background/80 p-3 rounded-xl">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Location</p>
                                        <p className="font-medium text-sm">{offerDetails.work_location}</p>
                                      </div>
                                    )}
                                    {offerDetails.probation_period && (
                                      <div className="bg-background/80 p-3 rounded-xl">
                                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Probation</p>
                                        <p className="font-medium text-sm">{offerDetails.probation_period}</p>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Company Name Mismatch Warning */}
                              {companyMismatchError && (
                                <div className="p-5 rounded-2xl border-2 bg-amber-50 dark:bg-amber-950/30 border-amber-200 dark:border-amber-800">
                                  {/* Header with Score */}
                                  <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                      <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-amber-500">
                                        <Shield className="h-6 w-6 text-white" />
                                      </div>
                                      <div>
                                        <h4 className="font-semibold text-amber-800 dark:text-amber-200">
                                          Suspicious: Company Mismatch
                                        </h4>
                                        <p className="text-xs text-muted-foreground">Company verification check</p>
                                      </div>
                                    </div>
                                    <div className="px-4 py-2 rounded-xl text-lg font-bold bg-amber-500 text-white">
                                      !
                                    </div>
                                  </div>
                                  
                                  {/* Mismatch Details */}
                                  <div className="mt-4 pt-4 border-t border-amber-200 dark:border-amber-800">
                                    <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wide mb-2">Issue Detected</p>
                                    <div className="space-y-1.5">
                                      <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
                                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                        <span>You entered: <strong>"{formData.company}"</strong></span>
                                      </div>
                                      <div className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
                                        <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                        <span>Offer letter shows: <strong>"{offerDetails?.company_name}"</strong></span>
                                      </div>
                                    </div>
                                  </div>
                                  
                                  {/* Help text */}
                                  <p className="mt-3 text-xs text-muted-foreground italic bg-white/50 dark:bg-black/20 p-2 rounded-lg">
                                    Please update the company name in Step 2 to match your offer letter, or upload the correct offer letter.
                                  </p>
                                </div>
                              )}
                              
                              {/* Extracted Salary Display - Full Breakdown */}
                              {salaryAutoFilled && (salaryBreakdown || extractedSalary) && (
                                <div className="p-5 bg-secondary/30 dark:bg-secondary/20 border border-border rounded-2xl">
                                  <div className="flex items-center gap-2 mb-4">
                                    <DollarSign className="h-5 w-5 text-foreground/70" />
                                    <h5 className="font-semibold text-sm">Salary Breakdown</h5>
                                  </div>
                                  
                                  {/* Full breakdown if available */}
                                  {salaryBreakdown ? (
                                    <div className="space-y-4">
                                      {/* Base Compensation */}
                                      {salaryBreakdown.base_compensation && Object.values(salaryBreakdown.base_compensation).some(v => v) && (
                                        <div>
                                          <h6 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Base Compensation</h6>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {salaryBreakdown.base_compensation.fixed_salary && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Fixed</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.base_compensation.fixed_salary/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.base_compensation.hra && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">HRA</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.base_compensation.hra/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.base_compensation.special_allowance && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Special</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.base_compensation.special_allowance/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.base_compensation.lta && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">LTA</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.base_compensation.lta/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.base_compensation.medical_allowance && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Medical</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.base_compensation.medical_allowance/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.base_compensation.food_allowance && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Food</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.base_compensation.food_allowance/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Variable Compensation */}
                                      {salaryBreakdown.variable_compensation && Object.values(salaryBreakdown.variable_compensation).some(v => v) && (
                                        <div>
                                          <h6 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Variable Pay</h6>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {salaryBreakdown.variable_compensation.variable_pay && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Variable</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.variable_compensation.variable_pay/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.variable_compensation.target_bonus && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Bonus</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.variable_compensation.target_bonus/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.variable_compensation.retention_bonus && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Retention</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.variable_compensation.retention_bonus/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* One-time Benefits */}
                                      {salaryBreakdown.one_time_benefits && Object.values(salaryBreakdown.one_time_benefits).some(v => v) && (
                                        <div>
                                          <h6 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">One-time Benefits</h6>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {salaryBreakdown.one_time_benefits.joining_bonus && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Joining</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.one_time_benefits.joining_bonus/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.one_time_benefits.relocation_allowance && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Relocation</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.one_time_benefits.relocation_allowance/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Long-term Incentives */}
                                      {salaryBreakdown.long_term_incentives && (salaryBreakdown.long_term_incentives.esops || salaryBreakdown.long_term_incentives.esops_vesting_period) && (
                                        <div>
                                          <h6 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Long-term Incentives</h6>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {salaryBreakdown.long_term_incentives.esops && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">ESOPs</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.long_term_incentives.esops/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.long_term_incentives.esops_vesting_period && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Vesting</p>
                                                <p className="font-medium text-sm">{salaryBreakdown.long_term_incentives.esops_vesting_period}</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Retirement & Insurance */}
                                      {salaryBreakdown.retirement_insurance && Object.values(salaryBreakdown.retirement_insurance).some(v => v) && (
                                        <div>
                                          <h6 className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Retirement & Insurance</h6>
                                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                            {salaryBreakdown.retirement_insurance.pf_contribution && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">PF</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.retirement_insurance.pf_contribution/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.retirement_insurance.gratuity && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Gratuity</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.retirement_insurance.gratuity/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.retirement_insurance.insurance_premium && (
                                              <div className="bg-background/80 p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Insurance</p>
                                                <p className="font-mono font-bold text-sm">₹{(salaryBreakdown.retirement_insurance.insurance_premium/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Totals - Highlighted */}
                                      {salaryBreakdown.totals && (salaryBreakdown.totals.total_ctc || salaryBreakdown.totals.gross_salary) && (
                                        <div className="pt-4 mt-4 border-t border-border">
                                          <div className="flex flex-wrap gap-3">
                                            {salaryBreakdown.totals.gross_salary && (
                                              <div className="flex-1 min-w-[120px] bg-background p-3 rounded-xl border border-border">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Gross Salary</p>
                                                <p className="font-mono font-bold text-lg">₹{(salaryBreakdown.totals.gross_salary/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.totals.total_ctc && (
                                              <div className="flex-1 min-w-[120px] bg-foreground text-background p-3 rounded-xl">
                                                <p className="text-[10px] uppercase tracking-wider opacity-70 mb-1">Total CTC</p>
                                                <p className="font-mono font-bold text-xl">₹{(salaryBreakdown.totals.total_ctc/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                            {salaryBreakdown.totals.in_hand_salary && (
                                              <div className="flex-1 min-w-[120px] bg-background p-3 rounded-xl border border-border">
                                                <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">In-Hand</p>
                                                <p className="font-mono font-bold text-lg">₹{(salaryBreakdown.totals.in_hand_salary/100000).toFixed(1)}L</p>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  ) : extractedSalary && (
                                    /* Fallback to simplified salary if full breakdown not available */
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
                                      {extractedSalary.fixed_salary && (
                                        <div>
                                          <p className="text-xs text-blue-600">Fixed Salary</p>
                                          <p className="font-mono font-bold">₹{(extractedSalary.fixed_salary/100000).toFixed(2)}L</p>
                                        </div>
                                      )}
                                      {extractedSalary.variable_pay > 0 && (
                                        <div>
                                          <p className="text-xs text-blue-600">Variable Pay</p>
                                          <p className="font-mono font-bold">₹{(extractedSalary.variable_pay/100000).toFixed(2)}L</p>
                                        </div>
                                      )}
                                      {extractedSalary.esops > 0 && (
                                        <div>
                                          <p className="text-xs text-blue-600">ESOPs</p>
                                          <p className="font-mono font-bold">₹{(extractedSalary.esops/100000).toFixed(2)}L</p>
                                        </div>
                                      )}
                                      {extractedSalary.joining_bonus > 0 && (
                                        <div>
                                          <p className="text-xs text-blue-600">Joining Bonus</p>
                                          <p className="font-mono font-bold">₹{(extractedSalary.joining_bonus/100000).toFixed(2)}L</p>
                                        </div>
                                      )}
                                      {extractedSalary.total_ctc && (
                                        <div className="col-span-2 md:col-span-3 pt-2 border-t border-blue-200">
                                          <p className="text-xs text-blue-600">Total CTC</p>
                                          <p className="font-mono font-bold text-lg">₹{(extractedSalary.total_ctc/100000).toFixed(2)}L</p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                  
                                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-4 italic">
                                    These values were extracted automatically and cannot be manually edited for verification integrity.
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="relative">
                              {uploadingOfferLetter ? (
                                /* Document Scanning Loader */
                                <div className="flex flex-col items-center justify-center p-10 border-2 border-primary/20 rounded-xl bg-gradient-to-b from-primary/5 to-transparent">
                                  {/* Document with scanning effect */}
                                  <div className="relative w-24 h-32 mb-6">
                                    {/* Document shape */}
                                    <div className="absolute inset-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                      {/* Document lines */}
                                      <div className="absolute top-4 left-3 right-3 space-y-2">
                                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
                                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-4/5"></div>
                                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
                                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-3/5"></div>
                                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-4/5"></div>
                                        <div className="h-2 bg-gray-200 dark:bg-gray-600 rounded w-full"></div>
                                      </div>
                                      {/* Scanning line animation */}
                                      <div className="absolute inset-x-0 h-1 bg-gradient-to-r from-transparent via-primary to-transparent animate-[scan_2s_ease-in-out_infinite]"></div>
                                    </div>
                                    {/* Corner fold */}
                                    <div className="absolute top-0 right-0 w-6 h-6 bg-gray-100 dark:bg-gray-700 rounded-bl-lg shadow-inner"></div>
                                    {/* Glow effect */}
                                    <div className="absolute -inset-2 bg-primary/20 rounded-xl blur-xl animate-pulse"></div>
                                  </div>
                                  
                                  {/* Status text */}
                                  <div className="text-center space-y-2">
                                    <p className="text-lg font-semibold text-foreground">Scanning Document</p>
                                    <p className="text-sm text-muted-foreground">AI is extracting salary details</p>
                                  </div>
                                  
                                  {/* Progress dots */}
                                  <div className="flex items-center gap-1.5 mt-5">
                                    <div className="w-2 h-2 rounded-full bg-primary animate-[bounce_1s_ease-in-out_infinite]"></div>
                                    <div className="w-2 h-2 rounded-full bg-primary animate-[bounce_1s_ease-in-out_0.2s_infinite]"></div>
                                    <div className="w-2 h-2 rounded-full bg-primary animate-[bounce_1s_ease-in-out_0.4s_infinite]"></div>
                                  </div>
                                  
                                  {/* Processing steps */}
                                  <div className="mt-6 space-y-2 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-2">
                                      <div className="w-4 h-4 rounded-full bg-green-500 flex items-center justify-center">
                                        <Check className="w-3 h-3 text-white" />
                                      </div>
                                      <span>Document uploaded</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <div className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin"></div>
                                      <span>Analyzing content...</span>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                /* Upload Area */
                                <label className="flex flex-col items-center justify-center p-8 cursor-pointer hover:bg-primary/5 transition-all duration-200 border-2 border-dashed border-muted-foreground/30 hover:border-primary/50 rounded-lg">
                                  <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
                                    <FileText className="h-8 w-8 text-red-500" />
                                  </div>
                                  <span className="text-base font-medium">Upload Offer Letter</span>
                                  <span className="text-sm text-muted-foreground mt-1">Click to browse or drag & drop</span>
                                  <div className="mt-3 px-3 py-1.5 bg-red-100 text-red-700 text-xs font-medium rounded">
                                    PDF format only • Max 5MB
                                  </div>
                                  <p className="text-xs text-muted-foreground mt-3 text-center max-w-xs">
                                    AI will extract salary details and verify document authenticity
                                  </p>
                                  <Input
                                    type="file"
                                    accept=".pdf,application/pdf"
                                    onChange={handleOfferLetterUpload}
                                    disabled={uploadingOfferLetter}
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    data-testid="offer-letter-input"
                                  />
                                </label>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Show internship duration if applicable */}
                        {formData.placement_type === "internship" && salaryAutoFilled && (
                          <div>
                            <Label>Duration (Months) *</Label>
                            <Select 
                              value={formData.internship_duration} 
                              onValueChange={(v) => handleInputChange('internship_duration', v)}
                            >
                              <SelectTrigger className="h-12 mt-2" data-testid="duration-select">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[1, 2, 3, 4, 5, 6].map(m => (
                                  <SelectItem key={m} value={m.toString()}>
                                    {m} {m === 1 ? 'Month' : 'Months'}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                      </div>
                    ) : (
                      /* Manual Entry Section */
                      <div className="space-y-4">
                        <h4 className="font-medium">Enter your salary details</h4>
                        <p className="text-sm text-muted-foreground">
                          All fields are required. Enter 0 if not applicable.
                        </p>
                        
                        {formData.placement_type === "internship" ? (
                          <div className="space-y-4">
                            <div>
                              <Label>Monthly Stipend (INR) *</Label>
                              <Input
                                type="number"
                                placeholder="e.g., 80000"
                                value={formData.internship_stipend}
                                onChange={(e) => handleInputChange('internship_stipend', e.target.value)}
                                className="h-12 mt-2 font-mono"
                                data-testid="stipend-input"
                              />
                            </div>
                            <div>
                              <Label>Duration (Months) *</Label>
                              <Select 
                                value={formData.internship_duration} 
                                onValueChange={(v) => handleInputChange('internship_duration', v)}
                              >
                                <SelectTrigger className="h-12 mt-2" data-testid="duration-select">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {[1, 2, 3, 4, 5, 6].map(m => (
                                    <SelectItem key={m} value={m.toString()}>
                                      {m} {m === 1 ? 'Month' : 'Months'}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <Label>Fixed Salary (CTC) *</Label>
                              <Input
                                type="number"
                                placeholder="e.g., 1200000"
                                value={formData.fixed}
                                onChange={(e) => handleInputChange('fixed', e.target.value)}
                                className="h-12 mt-2 font-mono"
                                data-testid="fixed-input"
                                required
                              />
                            </div>
                            <div>
                              <Label>Variable / Performance Bonus *</Label>
                              <Input
                                type="number"
                                placeholder="e.g., 200000 (enter 0 if none)"
                                value={formData.variable}
                                onChange={(e) => handleInputChange('variable', e.target.value)}
                                className="h-12 mt-2 font-mono"
                                data-testid="variable-input"
                                required
                              />
                            </div>
                            <div>
                              <Label>ESOPs / RSUs (Annualized) *</Label>
                              <Input
                                type="number"
                                placeholder="e.g., 500000 (enter 0 if none)"
                                value={formData.esops}
                                onChange={(e) => handleInputChange('esops', e.target.value)}
                                className="h-12 mt-2 font-mono"
                                data-testid="esops-input"
                                required
                              />
                            </div>
                            <div>
                              <Label>Joining Bonus *</Label>
                              <Input
                                type="number"
                                placeholder="e.g., 100000 (enter 0 if none)"
                                value={formData.joining_bonus}
                                onChange={(e) => handleInputChange('joining_bonus', e.target.value)}
                                className="h-12 mt-2 font-mono"
                                data-testid="bonus-input"
                                required
                              />
                            </div>
                            <div className="md:col-span-2">
                              <Label>Relocation Allowance *</Label>
                              <Input
                                type="number"
                                placeholder="e.g., 50000 (enter 0 if none)"
                                value={formData.relocation}
                                onChange={(e) => handleInputChange('relocation', e.target.value)}
                                className="h-12 mt-2 font-mono"
                                data-testid="relocation-input"
                                required
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Total Preview */}
                <div className="mt-6 p-4 bg-secondary border border-border">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground font-medium">
                      {formData.placement_type === "internship" ? "Total Internship Value" : "Total CTC"}
                    </span>
                    <span className="font-mono text-2xl font-bold" data-testid="total-ctc">
                      {formatCurrency(calculateTotal())}
                    </span>
                  </div>
                  
                  {/* CTC Verification Indicator */}
                  {!salaryAutoFilled && calculateTotal() > 0 && formData.company && formData.role && (
                    <div className="mt-4 pt-4 border-t border-border">
                      {verifyingCtc ? (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          Verifying against market data...
                        </div>
                      ) : ctcVerification ? (
                        <div className="space-y-3">
                          {/* Confidence Score */}
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-muted-foreground">CTC Confidence Score</span>
                            <div className="flex items-center gap-2">
                              <div className="w-24 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full rounded-full transition-all ${
                                    ctcVerification.confidence_score >= 70 ? 'bg-green-500' :
                                    ctcVerification.confidence_score >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                                  }`}
                                  style={{ width: `${ctcVerification.confidence_score}%` }}
                                />
                              </div>
                              <span className={`text-sm font-mono font-bold ${
                                ctcVerification.confidence_score >= 70 ? 'text-green-600 dark:text-green-400' :
                                ctcVerification.confidence_score >= 40 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
                              }`}>
                                {ctcVerification.confidence_score}%
                              </span>
                            </div>
                          </div>
                          
                          {/* Market Range Check */}
                          <div className={`p-3 rounded-lg text-sm ${
                            ctcVerification.market_check.flag_level === 'green' 
                              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200' 
                              : ctcVerification.market_check.flag_level === 'yellow'
                              ? 'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-800 dark:text-yellow-200'
                              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                          }`}>
                            <div className="flex items-start gap-2">
                              {ctcVerification.market_check.flag_level === 'green' ? (
                                <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              ) : ctcVerification.market_check.flag_level === 'yellow' ? (
                                <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              ) : (
                                <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              )}
                              <div>
                                <p className="font-medium">Market Range Check</p>
                                <p className="text-xs mt-1 opacity-80">{ctcVerification.market_check.flag_reason}</p>
                                <p className="text-xs mt-1 opacity-60">
                                  Expected range: ₹{(ctcVerification.market_check.market_range.min/100000).toFixed(1)}L - ₹{(ctcVerification.market_check.market_range.max/100000).toFixed(1)}L
                                  ({ctcVerification.market_check.market_range.role_category}, {ctcVerification.market_check.market_range.company_tier.replace('_', ' ')})
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          {/* Peer Corroboration */}
                          <div className={`p-3 rounded-lg text-sm ${
                            ctcVerification.peer_corroboration.is_corroborated
                              ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-200'
                              : 'bg-gray-50 dark:bg-gray-800/50 text-gray-700 dark:text-gray-300'
                          }`}>
                            <div className="flex items-start gap-2">
                              <Building2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="font-medium">Peer Corroboration</p>
                                {ctcVerification.peer_corroboration.has_peer_data ? (
                                  <>
                                    <p className="text-xs mt-1 opacity-80">
                                      {ctcVerification.peer_corroboration.peer_count} verified reports from {formData.company}
                                    </p>
                                    <p className="text-xs mt-1 opacity-60">
                                      Peer range: {ctcVerification.peer_corroboration.peer_ctc_range && 
                                        `₹${(ctcVerification.peer_corroboration.peer_ctc_range.min/100000).toFixed(1)}L - ₹${(ctcVerification.peer_corroboration.peer_ctc_range.max/100000).toFixed(1)}L`
                                      }
                                      {ctcVerification.peer_corroboration.deviation_from_peers !== null && 
                                        ` (${ctcVerification.peer_corroboration.deviation_from_peers > 0 ? '+' : ''}${ctcVerification.peer_corroboration.deviation_from_peers.toFixed(1)}% from median)`
                                      }
                                    </p>
                                  </>
                                ) : (
                                  <p className="text-xs mt-1 opacity-80">
                                    No peer data available for {formData.company} yet
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Warning if flagged */}
                          {ctcVerification.requires_review && (
                            <div className="p-3 bg-red-100 dark:bg-red-900/30 rounded-lg text-red-800 dark:text-red-200 text-xs">
                              <p className="font-medium">⚠️ This CTC will be flagged for additional review</p>
                              <p className="mt-1 opacity-80">Additional verification documents may be required during the review process.</p>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Step 4: Verification Info */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold uppercase">Verification Details</h2>
                    <p className="text-sm text-muted-foreground">Provide details for automatic verification</p>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 text-sm text-blue-800 mb-6">
                  <p className="font-medium mb-1">Verification Requirements:</p>
                  <p className="mb-2">Your submission will be verified only when <strong>BOTH</strong> conditions are met:</p>
                  <ul className="list-disc ml-4 space-y-1">
                    <li><strong>Company Email OTP</strong> - Proves you actually work at {formData.company || 'the company'}</li>
                    <li><strong>LinkedIn Verification</strong> - Confirms your company & role match what you submitted</li>
                  </ul>
                </div>

                <div className="space-y-4">
                  <div>
                    <Label className="flex items-center gap-2">
                      <Linkedin className="h-4 w-4 text-sky-600" />
                      LinkedIn Profile URL *
                    </Label>
                    <Input
                      placeholder="https://linkedin.com/in/your-profile"
                      value={formData.linkedin_url}
                      onChange={(e) => handleInputChange('linkedin_url', e.target.value)}
                      className={`h-12 mt-2 ${linkedinNameError ? 'border-amber-500 focus:border-amber-500' : ''}`}
                      data-testid="linkedin-input"
                    />
                    {linkedinNameError ? (
                      <div className="flex items-start gap-2 mt-2 text-amber-600 text-sm">
                        <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">LinkedIn profile doesn't match your name</p>
                          <p className="text-xs mt-0.5">{linkedinNameError}</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">
                        Make sure your current position on LinkedIn matches the company & role you're submitting
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-blue-600" />
                      Company Email *
                    </Label>
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={formData.company_email}
                      onChange={(e) => handleCompanyEmailChange(e.target.value)}
                      className={`h-12 mt-2 ${emailDomainError ? 'border-red-500 focus:border-red-500' : ''}`}
                      data-testid="company-email-input"
                    />
                    {emailDomainError ? (
                      <div className="flex items-start gap-2 mt-2 text-red-600 text-sm">
                        <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Company name and company email don't match</p>
                          <p className="text-xs mt-0.5">{emailDomainError}</p>
                        </div>
                      </div>
                    ) : emailNameError ? (
                      <div className="flex items-start gap-2 mt-2 text-amber-600 text-sm">
                        <Shield className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Email doesn't match your name</p>
                          <p className="text-xs mt-0.5">{emailNameError}</p>
                        </div>
                      </div>
                    ) : formData.company_email && !formData.company ? (
                      <p className="text-xs text-blue-600 mt-1">
                        Company will be auto-detected from your email domain
                      </p>
                    ) : (
                      <p className="text-xs text-muted-foreground mt-1">
                        We'll send an OTP to verify you work at {formData.company || 'the company'}
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-green-600" />
                      College Email (Optional)
                    </Label>
                    <Input
                      type="email"
                      placeholder="you@college.edu"
                      value={formData.college_email}
                      onChange={(e) => handleInputChange('college_email', e.target.value)}
                      className="h-12 mt-2"
                      data-testid="college-email-input"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Verification Process */}
            {step === 5 && placementId && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 bg-primary text-primary-foreground flex items-center justify-center">
                    <Shield className="h-5 w-5" />
                  </div>
                  <div>
                    <h2 className="font-heading text-lg font-bold uppercase">Verify Your Submission</h2>
                    <p className="text-sm text-muted-foreground">Complete verification to get your submission approved</p>
                  </div>
                </div>

                {/* LinkedIn Verification - Auto verify on open */}
                <div className="border border-border rounded-xl overflow-hidden">
                  {/* Header */}
                  <div className="flex items-center justify-between p-4 bg-secondary/30">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        verificationState.linkedin_verified 
                          ? 'bg-green-100 dark:bg-green-900/30' 
                          : 'bg-sky-100 dark:bg-sky-900/30'
                      }`}>
                        <Linkedin className={`h-5 w-5 ${
                          verificationState.linkedin_verified ? 'text-green-600' : 'text-sky-600'
                        }`} />
                      </div>
                      <div>
                        <p className="font-medium">LinkedIn Verification</p>
                        <p className="text-xs text-muted-foreground">Verify your profile matches</p>
                      </div>
                    </div>
                    {verificationState.linkedin_verified && (
                      <span className="flex items-center gap-1.5 text-green-600 text-sm font-medium bg-green-50 dark:bg-green-950/30 px-3 py-1.5 rounded-full">
                        <CheckCircle className="h-4 w-4" />
                        Verified
                      </span>
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="p-4">
                    {verificationState.linkedin_verified ? (
                      <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-xl border border-green-200 dark:border-green-800">
                        <div className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-green-800 dark:text-green-200">Profile Verified Successfully</p>
                            <p className="text-xs text-green-600 dark:text-green-400 mt-1 break-all">{formData.linkedin_url}</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Instructions */}
                        <div className="p-4 bg-sky-50 dark:bg-sky-950/30 rounded-xl border border-sky-200 dark:border-sky-800">
                          <p className="text-sm font-medium text-sky-800 dark:text-sky-200 mb-2">
                            Verify your LinkedIn shows:
                          </p>
                          <div className="space-y-2">
                            <div className="flex items-center gap-2 text-sm text-sky-700 dark:text-sky-300">
                              <div className="w-5 h-5 rounded bg-sky-200 dark:bg-sky-800 flex items-center justify-center text-xs font-bold">1</div>
                              <span><strong>Company:</strong> {formData.company || 'Your company'}</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-sky-700 dark:text-sky-300">
                              <div className="w-5 h-5 rounded bg-sky-200 dark:bg-sky-800 flex items-center justify-center text-xs font-bold">2</div>
                              <span><strong>Role:</strong> {formData.role || 'Your role'}</span>
                            </div>
                          </div>
                        </div>
                        
                        {/* URL and Button */}
                        <div className="space-y-3">
                          <div className="p-3 bg-secondary/50 rounded-lg">
                            <p className="text-xs text-muted-foreground mb-1">Your LinkedIn URL:</p>
                            <p className="text-sm font-mono break-all">{formData.linkedin_url || 'Not entered'}</p>
                          </div>
                          
                          <Button
                            onClick={handleOpenLinkedInProfile}
                            disabled={verificationState.verifying_linkedin || !formData.linkedin_url}
                            className="w-full h-12 bg-sky-600 hover:bg-sky-700 text-white font-medium"
                            data-testid="verify-linkedin-btn"
                          >
                            {verificationState.verifying_linkedin ? (
                              <>
                                <Loader2 className="h-5 w-5 animate-spin mr-2" />
                                Verifying...
                              </>
                            ) : (
                              <>
                                <Linkedin className="h-5 w-5 mr-2" />
                                Open Profile & Verify
                              </>
                            )}
                          </Button>
                          
                          <p className="text-xs text-center text-muted-foreground">
                            This will open your LinkedIn profile in a new tab and verify it automatically
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Error Display */}
                    {verificationState.linkedin_error && !verificationState.linkedin_verified && (
                      <div className="mt-4 p-3 bg-red-50 dark:bg-red-950/30 rounded-lg border border-red-200 dark:border-red-800">
                        <div className="flex items-start gap-2">
                          <XCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-red-600 dark:text-red-400">{verificationState.linkedin_error}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Company Email Verification */}
                {formData.company_email && (
                  <div className="border border-border p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-blue-600" />
                        <div>
                          <p className="font-medium">Company Email Verification</p>
                          <p className="text-sm text-muted-foreground">{formData.company_email}</p>
                        </div>
                      </div>
                      {verificationState.company_email_verified ? (
                        <span className="flex items-center gap-1 text-green-600 text-sm">
                          <CheckCircle className="h-4 w-4" />
                          Verified
                        </span>
                      ) : !verificationState.company_otp_sent ? (
                        <Button
                          onClick={handleSendCompanyOTP}
                          disabled={verificationState.sending_otp || !canSendOTP}
                          variant="outline"
                          size="sm"
                          data-testid="send-otp-btn"
                        >
                          {verificationState.sending_otp ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Send OTP"
                          )}
                        </Button>
                      ) : null}
                    </div>
                    
                    {/* Email Domain Mismatch Error */}
                    {emailDomainError && !verificationState.company_email_verified && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 text-red-700 text-sm flex items-start gap-2">
                        <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium">Company name and company email don't match</p>
                          <p className="text-xs mt-1">{emailDomainError}</p>
                        </div>
                      </div>
                    )}
                    
                    {verificationState.company_otp_sent && !verificationState.company_email_verified && (
                      <div className="flex gap-2 mt-3">
                        <Input
                          placeholder="Enter 6-digit OTP"
                          value={verificationState.company_otp}
                          onChange={(e) => setVerificationState(prev => ({ ...prev, company_otp: e.target.value }))}
                          className="h-10 font-mono tracking-widest"
                          maxLength={6}
                          data-testid="otp-input"
                        />
                        <Button
                          onClick={handleVerifyCompanyOTP}
                          disabled={verificationState.verifying_company_otp || verificationState.company_otp.length !== 6}
                          data-testid="verify-otp-btn"
                        >
                          {verificationState.verifying_company_otp ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            "Verify"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Verification Status */}
                <div className={`p-4 border ${isFullyVerified ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                  {isFullyVerified ? (
                    <div className="flex items-center gap-3">
                      <CheckCircle className="h-6 w-6 text-green-600" />
                      <div>
                        <p className="font-medium text-green-800">Verification Complete!</p>
                        <p className="text-sm text-green-700">Your submission has been verified and will appear in the rankings.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3">
                      <XCircle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-medium text-amber-800">Verification Pending</p>
                        <p className="text-sm text-amber-700 mb-2">
                          Complete <strong>BOTH</strong> verification steps to get your submission approved:
                        </p>
                        <ul className="text-sm text-amber-700 space-y-1">
                          <li className="flex items-center gap-2">
                            {verificationState.company_email_verified ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <span className="h-4 w-4 rounded-full border-2 border-amber-400" />
                            )}
                            Company Email OTP
                          </li>
                          <li className="flex items-center gap-2">
                            {verificationState.linkedin_verified ? (
                              <CheckCircle className="h-4 w-4 text-green-600" />
                            ) : (
                              <span className="h-4 w-4 rounded-full border-2 border-amber-400" />
                            )}
                            LinkedIn Profile (Company & Role)
                          </li>
                        </ul>
                      </div>
                    </div>
                  )}
                </div>

                {isFullyVerified && (
                  <Button
                    onClick={() => navigate('/profile')}
                    className="w-full h-12 font-medium"
                    data-testid="done-btn"
                  >
                    Done - View My Submissions
                  </Button>
                )}
              </div>
            )}

            {/* Navigation Buttons */}
            {step < 5 && (
              <div className="flex justify-between mt-8 pt-6 border-t border-border">
                {step > 1 ? (
                  <Button 
                    variant="outline" 
                    onClick={() => setStep(step - 1)}
                    className="h-12 px-6 font-medium"
                    data-testid="prev-btn"
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Previous
                  </Button>
                ) : (
                  <div />
                )}

                {step < 4 ? (
                  <Button 
                    onClick={handleNext}
                    className="h-12 px-6 font-medium"
                    disabled={!validateStep(step)}
                    data-testid="next-btn"
                  >
                    Next
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : step === 4 ? (
                  <Button 
                    onClick={handleSubmitPlacement}
                    className="h-12 px-8 font-medium"
                    disabled={submitting || !validateStep(4)}
                    data-testid="submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit & Verify"
                    )}
                  </Button>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubmitPlacement;
